// Entry page - cryptic intro sequence
// The landing page is NOT a traditional homepage. It's a 30-60 second interactive experience.
// Full-screen black with terminal boot, novel excerpts, then "WHO IS CARLOS CHOPRA?"
// Click anywhere or press any key to proceed to the House.

import {
  runEntrySequence,
  cleanupEntrySequence,
  skipIntro
} from '../scripts/intro';
import { prefersReducedMotion } from '../scripts/animations';

// Track page state
let isActive = false;
let entryContainer: HTMLElement | null = null;
let scanlinesElement: HTMLElement | null = null;

/**
 * Render the entry page (landing/intro sequence)
 *
 * Entry sequence flow:
 * 1. 0s: Pure black screen with blinking cursor
 * 2. 2s: Terminal boot messages ("CONNECTING TO NODE...")
 * 3. 6-20s: Novel excerpts appear overlapping in different positions
 * 4. 20s: Fragments fade, "WHO IS CARLOS CHOPRA?" appears
 * 5. User clicks anywhere or presses any key to enter the House
 *
 * Returning visitors get an abbreviated sequence.
 * Reduced motion users get instant text display.
 */
export function renderEntry(): void {
  const app = document.getElementById('app');
  if (!app) return;

  // Clear previous content
  app.innerHTML = '';
  isActive = true;

  // Create entry container (full viewport)
  entryContainer = document.createElement('div');
  entryContainer.className = 'entry-container';
  entryContainer.setAttribute('role', 'main');
  entryContainer.setAttribute('aria-label', 'Introduction sequence');
  app.appendChild(entryContainer);

  // Add scanlines overlay for CRT effect (unless reduced motion)
  if (!prefersReducedMotion()) {
    scanlinesElement = createScanlinesOverlay();
    entryContainer.appendChild(scanlinesElement);
  }

  // Set up global keypress handler for accessibility
  setupGlobalKeyHandler();

  // Run the intro sequence - this handles:
  // - Full sequence (first-time visitors)
  // - Abbreviated sequence (returning visitors)
  // - Reduced motion sequence (accessibility)
  // - Click/keypress to proceed interactions
  runEntrySequence(entryContainer);
}

/**
 * Create the CRT scanlines overlay element
 */
function createScanlinesOverlay(): HTMLElement {
  const scanlines = document.createElement('div');
  scanlines.className = 'entry-scanlines entry-scanlines--animated';
  scanlines.setAttribute('aria-hidden', 'true');
  return scanlines;
}

/**
 * Set up global keyboard handler for entry sequence
 * Escape key: Skip intro immediately
 * Space/Enter: Proceed if available
 */
function setupGlobalKeyHandler(): void {
  const keyHandler = (e: KeyboardEvent) => {
    if (!isActive) return;

    // Escape always skips immediately
    if (e.key === 'Escape') {
      e.preventDefault();
      skipIntro();
      return;
    }

    // Space and Enter are handled by intro.ts proceed handler
    // but we prevent default scroll behavior for space
    if (e.key === ' ') {
      e.preventDefault();
    }
  };

  document.addEventListener('keydown', keyHandler);

  // Store reference for cleanup
  if (entryContainer) {
    (entryContainer as any).__globalKeyHandler = keyHandler;
  }
}

/**
 * Cleanup entry page
 * Called by router when navigating away from entry page
 */
export function cleanupEntry(): void {
  isActive = false;

  // Clean up intro sequence (animations, timers, event listeners)
  cleanupEntrySequence();

  // Remove global key handler
  if (entryContainer) {
    const handler = (entryContainer as any).__globalKeyHandler;
    if (handler) {
      document.removeEventListener('keydown', handler);
    }
  }

  // Clear references
  entryContainer = null;
  scanlinesElement = null;
}

/**
 * Check if the entry page is currently active
 */
export function isEntryActive(): boolean {
  return isActive;
}

/**
 * Get the entry container element
 */
export function getEntryContainer(): HTMLElement | null {
  return entryContainer;
}

/**
 * Force skip the intro (useful for testing or direct navigation)
 */
export function forceSkipEntry(): void {
  if (isActive) {
    skipIntro();
  }
}
